/**
 * Phase 1 Type Definitions
 * Simple types for local token dragging without networking
 */

/**
 * Grid coordinates (in grid squares, not pixels)
 */
export interface GridCoordinates {
  x: number
  y: number
}

/**
 * Pixel coordinates for rendering
 */
export interface PixelCoordinates {
  x: number
  y: number
}

/**
 * Token entity - represents a game piece on the map
 */
export interface Token {
  id: string
  mapId: string // Phase 6: which map this token belongs to
  name: string
  x: number // grid coordinate
  y: number // grid coordinate
  width: number // grid squares (1 for medium creature)
  height: number // grid squares (1 for medium creature)
  color: string // hex color for simple tokens
  imageUrl?: string // optional token image (Phase 1 uses colors only)
  ownerUserId?: string | null // user who owns this token (for permissions in Phase 2+)
  visionRadius?: number // Phase 3: vision radius in grid squares (default: 6 for PCs, 0 for NPCs)
  characterId?: string | null // Link to character sheet on main platform
  // GM-controlled disclosure flag (server source of truth, default 'visible').
  // 'visible' = a normal token that obeys the map's fog-of-war and
  // line-of-sight rules; 'gm_only' = never shown to players. When absent we
  // treat the token as 'visible' to match the server default. (A legacy
  // 'hidden' value may still arrive from old data; it is normalized to
  // 'visible' on ingest and resolves the same way.)
  visibility?: 'visible' | 'gm_only'
  // GM-controlled toggle for whether PLAYERS may see this token's HP bar.
  // SEPARATE from `visibility` (which gates the whole token) — this only
  // gates the HP bar overlay. The GM always sees the HP bar regardless.
  // When absent we treat it as `true` (visible) to preserve the prior
  // behavior for already-existing tokens; the GM hides NPC HP bars by
  // explicitly flipping this off.
  hpBarVisibleToPlayers?: boolean
  // Token facing in degrees clockwise (0-359). GM-set; the canvas rotates the
  // portrait sprite by this. Absent = 0 (unrotated) for back-compat.
  rotation?: number
  // Status/condition keys (e.g. ['poisoned','prone']); resolved to name + icon
  // via the status registry. Absent/empty = no statuses.
  statuses?: string[]
  // When set, this token rigidly follows the leader token of this id on the
  // same map — it shifts by the leader's move delta. GM-set. Null/absent = no
  // leader.
  followsTokenId?: string | null
  // Light radius in grid squares. GM sets for any token; players set for their
  // own tokens (torch). null / absent = no light configured.
  lightRadius?: number | null
  // Whether the token's configured light is currently active. Only meaningful
  // when lightRadius is set. GM or owning player may toggle.
  torchActive?: boolean
  // Vision-type toggles. GM-set. Absent = false (no special vision).
  // lowLightVision: token sees dim light as bright. darkvision: token sees in
  // darkness.
  lowLightVision?: boolean
  darkvision?: boolean
  /**
   * Per-token combat state for mob tokens (character.isMob === true).
   * When non-null, this token tracks its own HP/statuses independently from
   * the shared character sheet. When null (or absent), stats are read from
   * the character sheet (non-mob / linked token).
   */
  instanceStats?: { hp: { current: number; max: number; temp: number }; statuses: string[] } | null
}

/**
 * Barrier entity - vision/movement-blocking polygon (Phase 3)
 */
export interface Barrier {
  id: string
  mapId: string
  points: Array<{ x: number; y: number }> // Grid coordinates of vertices (closed polygon)
  color?: string // Optional color override (default: red)
  blocksVision?: boolean // Whether this barrier blocks vision (default: true)
  blocksMovement?: boolean // Whether this barrier blocks movement (default: false)
  closed?: boolean // false = open polyline wall (no closing edge); undefined/true = legacy closed polygon
}

/**
 * Vision mode for maps (Phase 3).
 *
 * @deprecated The vision system no longer reads a single mode enum. Dynamic
 * line-of-sight is its own boolean (`MapConfig.losEnabled`) and fog-of-war is
 * always available, layering on top. The `vision_mode` DB column is retained
 * but stale. This type lingers only for the wire-compat fields on legacy
 * socket payloads.
 */
export type VisionMode = 'manual_fog' | 'dynamic_los'

/**
 * Tool mode. One mutually-exclusive selection across the whole tabletop
 * UI — `select` is the default cursor; `ruler` measures distance;
 * `barrier` and `fog` are GM-only but always available (no longer gated by a
 * vision mode). Whether the fog tool paints reveal vs hide is a
 * sub-setting (`FogMode`), not a separate tool, so the toolbar is one
 * radio group.
 */
export type ToolMode = 'select' | 'ruler' | 'barrier' | 'fog' | 'light'

/**
 * Measurement-template shape for the ruler tool. `line` is the classic
 * straight-line ruler (default); the others are D&D 5e area templates
 * sized by click-drag. Only meaningful when `toolMode === 'ruler'`.
 */
export type MeasurementShape = 'line' | 'cone' | 'circle' | 'square'

/** Reveal vs hide sub-setting for the fog brush. Only meaningful when
 *  `toolMode === 'fog'`. */
export type FogMode = 'reveal' | 'hide'

/**
 * Map configuration
 */
export interface MapConfig {
  id: string
  sessionId: string // which session this map belongs to
  name: string
  width: number // grid squares
  height: number // grid squares
  gridSize: number // pixels per grid square (default: 50)
  gridColor: string // Phase 6: grid line color (default: #000000)
  gridOpacity: number // Phase 6: grid line opacity 0-1 (default: 0.3)
  showGrid: boolean // Phase 6: toggle grid visibility (default: true)
  gridOffsetX: number // Phase 6: grid horizontal offset in pixels (default: 0)
  gridOffsetY: number // Phase 6: grid vertical offset in pixels (default: 0)
  backgroundOffsetX: number // background image horizontal offset in pixels (default: 0)
  backgroundOffsetY: number // background image vertical offset in pixels (default: 0)
  backgroundScaleX: number // background image horizontal scale multiplier (default: 1)
  backgroundScaleY: number // background image vertical scale multiplier (default: 1)
  backgroundFit?: 'stretch' | 'contain' // how the bg fills the map: 'stretch' fills the grid distorting aspect (default), 'contain' fits preserving aspect (letterbox)
  backgroundUrl?: string // optional background image
  backgroundColor: string // fallback color
  isActive: boolean // Phase 6: whether this is the active map
  /** @deprecated Use `losEnabled`. Retained for legacy hydration tolerance. */
  visionMode?: VisionMode // Phase 3: vision system mode (deprecated)
  losEnabled?: boolean // dynamic line-of-sight on/off (independent of fog)
  showBarriers?: boolean // Phase 3: whether to show barriers
  lightingEnabled?: boolean // dynamic lighting on/off. OFF = full light everywhere; ON = dark unless lit.
  renderMode?: 'dimmed' | 'darkness' // player fog appearance: 'darkness' fully hides content (default), 'dimmed' lets terrain show faintly through
}

/**
 * Standalone light source placed by the GM on a map.
 */
export interface LightSource {
  id: string
  mapId: string
  x: number // grid coordinate (center)
  y: number // grid coordinate (center)
  radius: number // light radius in grid squares (legacy; kept for back-compat)
  brightRadius: number // bright band radius in grid squares
  dimRadius: number // dim band radius in grid squares
  color: string // light tint, hex
  falloff: number // edge falloff, 0-100 percent
}

/**
 * Drag state for token being dragged
 */
export interface DragState {
  tokenId: string
  startX: number // original grid x
  startY: number // original grid y
  currentX: number // current pixel x (while dragging)
  currentY: number // current pixel y (while dragging)
  isDragging: boolean
}

/**
 * Application state for Phase 1-6
 */
export interface VTTState {
  maps: MapConfig[] // Phase 6: all maps in session
  activeMapId: string | null // Phase 6: ID of currently active map
  tokens: Token[]
  dragState: DragState | null
  // Multi-select: list of currently-selected token IDs. Empty array
  // means nothing is selected. Rubber-band, shift-click toggle, and
  // bulk delete/move all read and mutate this list.
  selectedTokenIds: string[]
  barriers: Barrier[] // Phase 3: vision-blocking barriers
}

/**
 * Phase 5: Chat & Dice Types
 */

/**
 * Individual die roll result
 */
export interface DieRoll {
  type: string        // e.g., "StandardDice", "PercentileDice"
  value: number       // result value
  initialValue?: number // before modifiers/rerolls
  modifiers?: string[] // e.g., ["reroll", "!"]
}

/**
 * Chat message from a user
 */
export interface ChatMessage {
  messageId: string
  userId: string
  displayName: string
  message: string
  timestamp: string
  isWhisper: boolean
}

/**
 * Dice roll result
 */
export interface DiceRollMessage {
  rollId: string
  userId: string
  displayName: string
  /** Source of the roll (e.g. "Acrobatics", "Dexterity Save", "Initiative").
   *  Optional — raw dice-tray rolls are unlabeled and render as before. */
  label?: string
  notation: string
  total: number
  output: string // formatted output from dice-roller
  rolls: DieRoll[]
  timestamp: string
  isWhisper: boolean
}

/**
 * Union type for chat feed messages
 */
export type FeedMessage = ChatMessage | DiceRollMessage

/**
 * Type guard for chat messages
 */
export function isChatMessage(msg: FeedMessage): msg is ChatMessage {
  return 'message' in msg
}

/**
 * Type guard for dice roll messages
 */
export function isDiceRollMessage(msg: FeedMessage): msg is DiceRollMessage {
  return 'notation' in msg
}
